<template>
  <div class="api-docs-container">
    <div class="mb-6">
      <h2 class="text-2xl font-bold mb-4 dark:text-white">API Documentation</h2>
      <p class="mb-4 dark:text-gray-300">
        Explore the RESTful API endpoints that power the GeoProperty Hub platform.
        Select a service from the dropdown to view its specific documentation.
      </p>
    </div>
    
    <div class="bg-gray-100 dark:bg-gray-800 p-4 rounded-lg mb-6">
      <div class="flex flex-col md:flex-row md:items-center mb-4">
        <label for="service-select" class="mr-3 font-medium dark:text-white">Select Service:</label>
        <select 
          id="service-select" 
          v-model="selectedService" 
          @change="updateDocFrame" 
          class="p-2 border rounded bg-white dark:bg-gray-700 dark:text-white dark:border-gray-600 focus:border-primary-500 focus:outline-none"
        >
          <option value="">All Services</option>
          <option v-for="service in services" :key="service.name" :value="service.path">
            {{ service.name }}
          </option>
        </select>
      </div>
      
      <div v-if="loading" class="flex justify-center py-8">
        <div class="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary-500"></div>
      </div>
      
      <div v-else-if="error" class="bg-red-100 dark:bg-red-900 p-4 rounded text-red-700 dark:text-red-300">
        <p>{{ error }}</p>
      </div>
    </div>
    
    <div class="api-frame-container bg-white dark:bg-gray-800 rounded-lg shadow overflow-hidden" style="height: 600px;">
      <iframe 
        ref="docFrame" 
        :src="docUrl" 
        class="w-full h-full border-0"
        @load="onFrameLoad"
      ></iframe>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted, computed } from 'vue';
import apiService from '../utils/api';

const selectedService = ref('');
const services = ref([]);
const loading = ref(true);
const error = ref(null);
const docFrame = ref(null);

const docUrl = computed(() => {
  if (selectedService.value) {
    return apiService.getSwaggerUrl(selectedService.value);
  }
  return apiService.getSwaggerUrl();
});

const fetchServices = async () => {
  try {
    loading.value = true;
    const response = await apiService.getMicroservices();
    if (response.success) {
      services.value = response.services || [];
    } else {
      error.value = response.message || 'Failed to load services';
    }
  } catch (err) {
    console.error('Error fetching services:', err);
    error.value = 'Error connecting to the API. Please try again later.';
  } finally {
    loading.value = false;
  }
};

const updateDocFrame = () => {
  loading.value = true;
  if (docFrame.value) {
    docFrame.value.src = docUrl.value;
  }
};

const onFrameLoad = () => {
  loading.value = false;
};

onMounted(async () => {
  await fetchServices();
  // Default loading is handled by iframe onload
});
</script>

<style scoped>
.api-docs-container {
  /* Add any additional styles needed */
}

.api-frame-container {
  /* Ensure the iframe container has a defined height */
  min-height: 600px;
}
</style>